import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface HighPerformanceBannerProps {
  adKey: string;
  width?: number;
  height?: number;
  id?: string;
  mobileWidth?: number;
  mobileHeight?: number;
  hideOnMobile?: boolean;
  requireGestureOnMobile?: boolean;
  useSandboxOnMobile?: boolean;
}

const BannerContainer = styled.div<{
  $width: number;
  $height: number;
  $mWidth: number;
  $mHeight: number;
  $id?: string;
  $hideOnMobile?: boolean;
}>`
  width: 100%;
  max-width: ${props => props.$width}px;
  min-height: ${props => props.$height}px;
  margin: 1rem auto;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 10;
  overflow: visible;
  background: transparent;
  border: 0;
  box-shadow: none;

  & iframe {
    border: 0 !important;
  }
  
  @media (max-width: 768px) {
    display: ${props => (props.$hideOnMobile ? 'none' : 'flex')};
    width: 100%;
    max-width: ${props => props.$mWidth}px;
    min-height: ${props => props.$mHeight}px;
    margin: ${props => (props.$id?.includes('home') || props.$id?.includes('top')
      ? '0.5rem auto'
      : '0.5rem auto')};
  }
`;

/**
 * HighPerformance Banner Component
 * Format publicitaire bannière 728x90
 */
const HighPerformanceBanner: React.FC<HighPerformanceBannerProps> = ({
  adKey,
  width = 728,
  height = 90,
  id = 'hp-banner',
  mobileWidth = 320,
  mobileHeight = 50,
  hideOnMobile = false,
  requireGestureOnMobile = false,
  useSandboxOnMobile = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptLoaded = useRef(false);
  const [mDims, setMDims] = useState<{ w: number; h: number }>({ w: mobileWidth, h: mobileHeight });
  const [hasGesture, setHasGesture] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const vw = typeof window !== 'undefined' ? window.innerWidth : mobileWidth;
    const mobileW = Math.min(Math.max(320, vw - 24), 360); // 320..360 en laissant des marges
    const effWidth = isMobile ? Math.round(mobileW) : width;
    const effHeight = isMobile ? Math.round(effWidth * (mobileHeight / mobileWidth)) : height; // préserver le ratio

    if (isMobile) {
      setMDims({ w: effWidth, h: effHeight });
    }

    // Ajouter un délai pour éviter les conflits entre plusieurs publicités
    const delay = id.includes('bottom') ? 500 : 0;
    
    // Security: sandbox any ad iframe injected into the container (mobile only)
    const applyIframeSafety = (ifr: HTMLIFrameElement) => {
      try {
        if (!isMobile) return;
        if (!ifr.getAttribute('sandbox')) {
          // Block popups on mobile
          ifr.setAttribute('sandbox', 'allow-scripts allow-same-origin');
        }
        ifr.setAttribute('referrerpolicy', 'no-referrer');
        try { (ifr as any).allow = 'autoplay; encrypted-media; picture-in-picture'; } catch {}
      } catch {}
    };
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === Node.ELEMENT_NODE) {
            const el = n as Element;
            if (el.tagName === 'IFRAME') applyIframeSafety(el as HTMLIFrameElement);
            el.querySelectorAll && el.querySelectorAll('iframe').forEach((ifr) => applyIframeSafety(ifr as HTMLIFrameElement));
          }
        });
      }
    });
    try { mo.observe(container, { childList: true, subtree: true }); } catch {}

    const injectAd = () => {
      // Prevent duplicate injections
      if (scriptLoaded.current) return;
      const isMob = isMobile;
      if (isMob && useSandboxOnMobile) {
        // Inject vendor inside a sandboxed iframe to prevent top-level redirects
        const iframe = document.createElement('iframe');
        iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
        iframe.setAttribute('referrerpolicy', 'no-referrer');
        iframe.style.width = `${effWidth}px`;
        iframe.style.height = `${effHeight}px`;
        iframe.style.border = '0';
        container.appendChild(iframe);
        const doc = iframe.contentDocument || (iframe as any).document;
        if (doc) {
          // Warm-up connection inside the sandboxed document
          try {
            const head = doc.head || doc.getElementsByTagName('head')[0] || doc.body;
            const pre = doc.createElement('link');
            pre.rel = 'preconnect';
            pre.href = 'https://www.highperformanceformat.com';
            (pre as any).crossOrigin = 'anonymous';
            head?.appendChild(pre);
          } catch {}
          const configScript = doc.createElement('script');
          configScript.type = 'text/javascript';
          configScript.text = `
            atOptions = {
              'key' : '${adKey}',
              'format' : 'iframe',
              'height' : ${effHeight},
              'width' : ${effWidth},
              'params' : {}
            };
          `;
          doc.body.appendChild(configScript);
          const invokeScript = doc.createElement('script');
          invokeScript.type = 'text/javascript';
          invokeScript.src = `https://www.highperformanceformat.com/${adKey}/invoke.js`;
          invokeScript.async = true;
          try { (invokeScript as any).referrerPolicy = 'no-referrer'; } catch {}
          try { (invokeScript as any).crossOrigin = 'anonymous'; } catch {}
          doc.body.appendChild(invokeScript);
        }
      } else {
        // Créer le script de configuration
        const configScript = document.createElement('script');
        configScript.type = 'text/javascript';
        configScript.text = `
          atOptions = {
            'key' : '${adKey}',
            'format' : 'iframe',
            'height' : ${effHeight},
            'width' : ${effWidth},
            'params' : {}
          };
        `;
        // Créer le script d'invocation
        const invokeScript = document.createElement('script');
        invokeScript.type = 'text/javascript';
        invokeScript.src = `https://www.highperformanceformat.com/${adKey}/invoke.js`;
        invokeScript.async = true;
        try { (invokeScript as any).referrerPolicy = 'no-referrer'; } catch {}
        try { (invokeScript as any).crossOrigin = 'anonymous'; } catch {}
        invokeScript.onload = () => { console.log('HighPerformance Banner loaded:', id); };
        invokeScript.onerror = () => { console.error('Failed to load HighPerformance Banner:', id); };
        // Ajouter les scripts au conteneur
        container.appendChild(configScript);
        container.appendChild(invokeScript);
      }
      // Mark as loaded to avoid double appends
      try { (container as any).dataset.hpLoaded = '1'; } catch {}
      scriptLoaded.current = true;
    };

    let timer: any = null;
    let onGesture: any = null;
    const isMobileDevice = isMobile;
    if (isMobileDevice && requireGestureOnMobile) {
      if (!hasGesture) {
        onGesture = () => {
          setHasGesture(true);
          try { window.removeEventListener('touchstart', onGesture, true); } catch {}
          try { window.removeEventListener('click', onGesture, true); } catch {}
          // Do not inject immediately here to avoid race with effect re-run
        };
        try { window.addEventListener('touchstart', onGesture, { capture: true, passive: true }); } catch {}
        try { window.addEventListener('click', onGesture, { capture: true, passive: true }); } catch {}
      } else {
        timer = setTimeout(injectAd, delay);
      }
    } else {
      timer = setTimeout(injectAd, delay);
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (container) {
        container.innerHTML = '';
      }
      try { mo.disconnect(); } catch {}
      if (onGesture) {
        try { window.removeEventListener('touchstart', onGesture, true); } catch {}
        try { window.removeEventListener('click', onGesture, true); } catch {}
      }
    };
  }, [adKey, width, height, id, mobileWidth, mobileHeight, requireGestureOnMobile, hasGesture]);

  return (
    <BannerContainer 
      ref={containerRef} 
      $width={width} 
      $height={height}
      $mWidth={mDims.w}
      $mHeight={mDims.h}
      $id={id}
      $hideOnMobile={hideOnMobile}
    />
  );
};

export default HighPerformanceBanner;
